import { CUSTOM_ELEMENTS_SCHEMA, Component, OnDestroy, OnInit } from '@angular/core';
import { IonicModule } from '@ionic/angular';
import { CupertinoPane, CupertinoSettings } from 'cupertino-pane';
import { DmStoreService } from 'src/app/store/dm-store.service';
import { scrollToSelected } from 'src/app/app.utils';
import { Subscription } from 'rxjs';
import { GroupBindStoreService } from 'src/app/store/group-bind-store.service';
import { UserProfile } from 'src/app/providers';
import { DeviceBindStoreService } from 'src/app/store/device-bind-store.service';
import { LocaLstorageService } from 'src/app/store/localstorage.service';
import { UserGroup } from 'src/app/model';
import { CommonModule } from '@angular/common';
import { ProjectSelectComponent } from '../project-select/project-select.component';
import { UmStoreService } from '../../store/um-store.service';
import { IonicSelectableComponent } from 'ionic-selectable';
import { FormsModule } from '@angular/forms';
import { OwnerSelectComponent } from '../owner-select/owner-select.component';

const drawerSettings: CupertinoSettings = {
  initialBreak: 'top',
  backdrop: true,
  backdropOpacity: 0.4,
  breaks: {
    top: { // Topper point that pane can reach
      enabled: true, // Enable or disable breakpoint
      height: 200, // Pane breakpoint height
      bounce: false, // Bounce pane on transition
    },
    middle: { enabled: false },
    bottom: {  // Topper point that pane can reach
      enabled: false, // Enable or disable breakpoint
    },
  },
  bottomClose: true,
  draggableOver: false,
  showDraggable: false,
  dragBy: ['nothing'],
};

@Component({
  selector: 'app-project-select-pane',
  templateUrl: 'project-select-pane.component.html',
  styleUrls: ['project-select-pane.component.scss'],
  standalone: true,
  imports: [IonicModule, CommonModule, FormsModule, ProjectSelectComponent, OwnerSelectComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ProjectSelectPaneComponent implements OnInit, OnDestroy {
  public ownerName?: string = '';
  public projectName?: string = '';
  public isAdmin = false;

  private drawer: CupertinoPane;
  private subscriptionProject: Subscription;
  private subscriptionOwner: Subscription;
  private subscriptionOwnersList: Subscription;

  public accurateGroupPositions$ = this.groupBindStore.accuratePositions$;
  public accurateDevicePosition$ = this.deviceBindStore.accurateDevicePosition$;

  public owner: number;
  public owners: UserGroup[] = [];

  public projects$ = this.umStore.projects$;
  public owners$ = this.umStore.owners$;

  public loading$ = this.dmStoreService.loading$;

  constructor(
    private dmStoreService: DmStoreService,
    private groupBindStore: GroupBindStoreService,
    private deviceBindStore: DeviceBindStoreService,
    private umStore: UmStoreService,
    private localStorageService: LocaLstorageService,
    private userProfile: UserProfile,
  ) { }

  async ngOnInit() {
    await this.localStorageService.initProject();
    this.drawer = new CupertinoPane('ion-drawer', {
      ...drawerSettings,
      events: {
        onBackdropTap: () => this.hideDrawer(),
        onWillDismiss: () => this.hideDrawer(),
      },
    });
    this.localStorageService.checkPreenabledOptions();

    this.isAdmin = await this.userProfile.isAdmin();
    const initUserGroup = this.localStorageService.selectedOwner;
    const initUserProject = this.localStorageService.selectedProject;
    this.owner = initUserGroup || Number((await this.userProfile.getUser() || {}).user_group);

    if (this.isAdmin) {
      await this.umStore.fetchOwners(this.owner);
    }

    await this.umStore.fetchProjects(
      this.isAdmin ? { owner_id: this.owner } : {},
      this.dmStoreService.preselectedValues.zoneId,
      true,
    );

    if (!initUserProject) {
      this.drawer.present();
    } else {
      this.umStore.selectProject(initUserProject);
    }

    this.drawer.preventDismiss(true);
    this.drawer.settings.buttonDestroy = false;

    this.subscriptionProject = this.umStore.selectedProject$.subscribe(project => {
      this.projectName = project?.name;

      if (project) {
        this.drawer.preventDismiss(false);
        this.drawer.settings.buttonDestroy = true;
        this.localStorageService.selectedProject = project.id;
        this.drawer.destroy({ animate: false });
        this.dmStoreService.initDevicesAndPositions(project.id);
      }
    });
    this.subscriptionOwner = this.umStore.selectedOwner$.subscribe(owner => {
      if (owner) {
        this.ownerName = owner.name;
        this.localStorageService.selectedOwner = owner.id;
      }
    });
    this.subscriptionOwnersList = this.umStore.owners$.subscribe(owners => {
      this.owners = owners.data;
      this.ownerName = this.owners.find(g => g.id === this.owner)?.name;
    });
  }

  ngOnDestroy() {
    this.subscriptionProject?.unsubscribe();
    this.subscriptionOwner?.unsubscribe();
    this.subscriptionOwnersList?.unsubscribe();
  }

  public async presentDrawer() {
    this.drawer.present({ animate: true });
    // stop loading devices
    this.dmStoreService.stopLoadingDevices();
  }

  private hideDrawer() {
    if (this.projectName) {
      this.drawer.hide();
    }
  }

  public ownerChange(event: { component: IonicSelectableComponent, value: number }) {
    this.owner = event.value;

    this.umStore.selectOwner(this.owner);
  }

  public onOwnerSelectOpen() {
    scrollToSelected();
  }

}
